import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ContentProvider } from "@/context/ContentContext";
import { ConfirmProvider } from "@/context/ConfirmContext";
import { Toaster } from "@/components/ui/sonner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { GlobalSupportWidget } from "@/components/shared/GlobalSupportWidget";

import Landing from "@/pages/public/Landing";
import ServicesPublic from "@/pages/public/ServicesPublic";
import { HowItWorks, Faq } from "@/pages/public/InfoPages";
import { LegalPage, Contact } from "@/pages/public/LegalContact";
import { Login, Register, GoogleAuthCallback } from "@/pages/public/Auth";
import VerifyEmail from "@/pages/public/VerifyEmail";
import AdminControlLogin from "@/pages/AdminControlLogin";
import { PaymentResult, NotFound } from "@/pages/public/PaymentResult";

import { DashboardLayout } from "@/components/layout/DashboardLayout";
import PartnerDashboard from "@/pages/dash/PartnerDashboard";
import PartnerBinaryTree from "@/pages/dash/PartnerBinaryTree";
import Dashboard from "@/pages/dash/Dashboard";
import NewOrder from "@/pages/dash/NewOrder";
import Refill from "@/pages/dash/Refill";
import Favorites from "@/pages/dash/Favorites";
import CatalogCustomer from "@/pages/dash/CatalogCustomer";
import Orders from "@/pages/dash/Orders";
import OrderDetail from "@/pages/dash/OrderDetail";
import Transactions from "@/pages/dash/Transactions";
import AddFunds from "@/pages/dash/AddFunds";
import ApiPage from "@/pages/dash/ApiPage";
import Support from "@/pages/dash/Support";
import Settings from "@/pages/dash/Settings";

import { AdminLayout } from "@/components/layout/AdminLayout";
import CommandCenter from "@/pages/admin/CommandCenter";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminServices from "@/pages/admin/AdminServices";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminPlatforms from "@/pages/admin/AdminPlatforms";
import AdminMapping from "@/pages/admin/AdminMapping";
import AdminAlerts from "@/pages/admin/AdminAlerts";
import AdminProviders from "@/pages/admin/AdminProviders";
import AdminPawaPay from "@/pages/admin/AdminPawaPay";
import AdminAfriPay from "@/pages/admin/AdminAfriPay";
import AdminPaymentNumbers from "@/pages/admin/AdminPaymentNumbers";
import AdminSmsProviders from "@/pages/admin/AdminSmsProviders";
import AdminUSDT from "@/pages/admin/AdminUSDT";
import AdminPaymentAlerts from "@/pages/admin/AdminPaymentAlerts";
import { AdminPayments, AdminTransactions, AdminRefunds, AdminAuditLogs } from "@/pages/admin/AdminSimplePages";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminAgents from "@/pages/admin/AdminAgents";
import AdminAgentDetail from "@/pages/admin/AdminAgentDetail";
import ChangePassword from "@/pages/public/ChangePassword";
import { AgentLayout } from "@/components/layout/AgentLayout";
import { PermRoute } from "@/components/PermRoute";
import AgentDashboard from "@/pages/agent/AgentDashboard";
import AgentHistory, { AgentTeamActivity } from "@/pages/agent/AgentHistory";
import AgentClients from "@/pages/agent/AgentClients";
import AgentSupport from "@/pages/agent/AgentSupport";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import AdminPaymentSettings from "@/pages/admin/AdminPaymentSettings";
import AdminContent from "@/pages/admin/AdminContent";
import AdminBinaryAudit from "@/pages/admin/AdminBinaryAudit";
import AdminRiskCenter from "@/pages/admin/AdminRiskCenter";
import AdminServiceQuality from "@/pages/admin/AdminServiceQuality";
import AdminCatalogueHealth from "@/pages/admin/AdminCatalogueHealth";
import AdminPartnerWithdrawals from "@/pages/admin/AdminPartnerWithdrawals";
import AdminAccountDeletions from "@/pages/admin/AdminAccountDeletions";

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
      <ContentProvider>
      <ConfirmProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/services" element={<ServicesPublic />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/terms" element={<LegalPage type="terms" />} />
          <Route path="/privacy" element={<LegalPage type="privacy" />} />
          <Route path="/refund-policy" element={<LegalPage type="refund" />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/control_SuperAdmin" element={<AdminControlLogin />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/payment/success" element={<PaymentResult kind="success" />} />
          <Route path="/payment/cancel" element={<PaymentResult kind="cancel" />} />
          <Route path="/payment/failed" element={<PaymentResult kind="failed" />} />

          {/* Customer dashboard */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="new-order" element={<NewOrder />} />
            <Route path="refill" element={<Refill />} />
            <Route path="favorites" element={<Favorites />} />
            <Route path="services" element={<CatalogCustomer />} />
            <Route path="orders" element={<Orders />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="add-funds" element={<AddFunds />} />
            <Route path="api" element={<ApiPage />} />
            <Route path="support" element={<Support />} />
            <Route path="settings" element={<Settings />} />
            <Route path="partner" element={<PartnerDashboard />} />
            <Route path="partner/binary-tree" element={<PartnerBinaryTree />} />
          </Route>

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute admin><AdminLayout /></ProtectedRoute>}>
            <Route index element={<CommandCenter />} />
            <Route path="dashboard" element={<CommandCenter />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="services" element={<AdminServices />} />
            <Route path="pricing" element={<AdminPricing />} />
            <Route path="platforms" element={<AdminPlatforms />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="mapping" element={<AdminMapping />} />
            <Route path="catalogue-health" element={<AdminCatalogueHealth />} />
            <Route path="alerts" element={<AdminAlerts />} />
            <Route path="providers" element={<AdminProviders />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="pawapay" element={<AdminPawaPay />} />
            <Route path="afripay" element={<AdminAfriPay />} />
            <Route path="payment-numbers" element={<AdminPaymentNumbers />} />
            <Route path="sms-providers" element={<AdminSmsProviders />} />
            <Route path="usdt" element={<AdminUSDT />} />
            <Route path="payment-alerts" element={<AdminPaymentAlerts />} />
            <Route path="transactions" element={<AdminTransactions />} />
            <Route path="refunds" element={<AdminRefunds />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="agents" element={<AdminAgents />} />
            <Route path="agents/:id" element={<AdminAgentDetail />} />
            <Route path="audit-logs" element={<AdminAuditLogs />} />
            <Route path="security" element={<AdminSecurity />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="payment-settings" element={<AdminPaymentSettings />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="content/:category" element={<AdminContent />} />
            <Route path="binary-audit" element={<AdminBinaryAudit />} />
            <Route path="risk" element={<AdminRiskCenter />} />
            <Route path="service-quality" element={<AdminServiceQuality />} />
            <Route path="partner-withdrawals" element={<AdminPartnerWithdrawals />} />
            <Route path="account-deletions" element={<AdminAccountDeletions />} />
          </Route>

          {/* Agent space (fully separated from admin) */}
          <Route path="/agent" element={<ProtectedRoute agent><AgentLayout /></ProtectedRoute>}>
            <Route index element={<AgentDashboard />} />
            <Route path="dashboard" element={<AgentDashboard />} />
            <Route path="services" element={<PermRoute perm="VIEW_SERVICES"><AdminServices /></PermRoute>} />
            <Route path="orders" element={<PermRoute perm="VIEW_ORDERS"><AdminOrders /></PermRoute>} />
            <Route path="providers" element={<PermRoute perm="VIEW_PROVIDERS"><AdminProviders /></PermRoute>} />
            <Route path="mapping" element={<PermRoute perm="VIEW_PROVIDER_SERVICES"><AdminMapping /></PermRoute>} />
            <Route path="support" element={<PermRoute perm="VIEW_TICKETS"><AgentSupport /></PermRoute>} />
            <Route path="clients" element={<PermRoute perm="VIEW_CLIENTS"><AgentClients /></PermRoute>} />
            <Route path="content" element={<PermRoute perm="VIEW_SITE_CONTENT"><AdminContent /></PermRoute>} />
            <Route path="content/:category" element={<PermRoute perm="VIEW_SITE_CONTENT"><AdminContent /></PermRoute>} />
            <Route path="quality" element={<PermRoute perm="QUALITY_VIEW"><AdminServiceQuality /></PermRoute>} />
            <Route path="team-activity" element={<PermRoute perm="VIEW_AGENT_ACTIVITY"><AgentTeamActivity /></PermRoute>} />
            <Route path="transactions" element={<PermRoute perm="VIEW_TRANSACTIONS"><AdminTransactions /></PermRoute>} />
            <Route path="refunds" element={<PermRoute perm="VIEW_REFUNDS"><AdminRefunds /></PermRoute>} />
            <Route path="payments" element={<PermRoute perm="VIEW_PAYMENTS"><AdminPayments /></PermRoute>} />
            <Route path="payment-numbers" element={<PermRoute perm="VIEW_PAYMENT_NUMBERS"><AdminPaymentNumbers /></PermRoute>} />
            <Route path="partner-withdrawals" element={<PermRoute perm="AFFILIATE_PAYOUT_VIEW"><AdminPartnerWithdrawals /></PermRoute>} />
            <Route path="audit-logs" element={<PermRoute perm="VIEW_AUDIT_LOGS"><AdminAuditLogs /></PermRoute>} />
            <Route path="history" element={<AgentHistory />} />
            <Route path="password" element={<ChangePassword voluntary />} />
          </Route>


          <Route path="*" element={<NotFound />} />
        </Routes>
        <GlobalSupportWidget />
      </BrowserRouter>
      </ConfirmProvider>
      </ContentProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
